import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { stronomModules } from "@/data/stronom-modules";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { StudioAppHeader } from "./StudioAppHeader";
import { StudioSidebar } from "./StudioSidebar";
import { StudioHeaderActionsProvider } from "./StudioHeaderActions";
import { StudioUploadProvider } from "./StudioUploadActions";
import { OrbitWatcher } from "@/features/footwear-studio/orbit/OrbitWatcher";

const MainLayout = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  
  // Check if debug mode is enabled
  const debugMode = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("debug") === "1";

  // Check if we're in studio app mode (all /footwear-studio/* pages)
  const isStudioApp = location.pathname.startsWith("/footwear-studio");

  const search = location.search;
  const navItems = [
    { label: "Home", to: "/" },
    { label: "Modules", to: "/design" },
    { label: "About us", to: "/about" },
    ...(debugMode ? [{ label: "Jobs", to: `/jobs${search}` }] : []),
    {
      label: "Contact",
      to: "mailto:contact@stronom.com?subject=STRONOM%20Partnership",
    },
  ];

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Apply body class for studio app pages (guaranteed to mount for all wizard steps)
  useEffect(() => {
    if (isStudioApp) {
      const body = document.body;
      body.classList.add("studio-wizard-active");
      
      return () => {
        body.classList.remove("studio-wizard-active");
      };
    }
  }, [isStudioApp]);

  // Wrap studio app with header actions provider
  const studioContent = (
    <>
      <StudioSidebar />
      <StudioAppHeader />
      <main
        data-studio-main=""
        className="flex-1 min-h-0 overflow-hidden pl-0 md:pl-14"
      >
        <Outlet />
      </main>
    </>
  );

  return (
    <div className={cn(
      "relative text-foreground",
      isStudioApp 
        ? "h-[100dvh] overflow-hidden flex flex-col bg-bgPage" 
        : "min-h-screen bg-transparent"
    )}>
      {/* Studio App - wrapped with header actions provider */}
      {isStudioApp && (
        <StudioHeaderActionsProvider>
          <StudioUploadProvider>
            <OrbitWatcher />
            {studioContent}
          </StudioUploadProvider>
        </StudioHeaderActionsProvider>
      )}

      {/* Marketing site header - hidden for studio app */}
      {!isStudioApp && (
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-4">
          <NavLink
            to="/"
            className="flex items-center gap-2 text-left flex-shrink-0"
          >
            <img
              src="https://cdn.builder.io/api/v1/image/assets%2Ffa2f15fda5e44932811d4e18209a60ad%2Fb283d612969645dfaf65f1ba46f4c55b?format=webp&width=800"
              alt="STRONOM"
              style={{ height: "60px", width: "auto" }}
            />
          </NavLink>

          <nav className="hidden items-center gap-1 rounded-full border border-border/40 bg-background/70 px-2 py-1 xl:flex">
            {navItems.map((item) => {
              const isMailto = item.to.startsWith("mailto:");
              if (isMailto) {
                return (
                  <a
                    key={item.label}
                    href={item.to}
                    className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition hover:text-foreground"
                  >
                    {item.label}
                  </a>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      "rounded-full px-4 py-2 text-sm font-medium transition-all",
                      isActive
                        ? "bg-stronom-design/15 text-stronom-design shadow-[0_0_22px_hsl(var(--color-design)_/_0.35)]"
                        : "text-muted-foreground hover:text-foreground",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-border/60 text-foreground transition xl:hidden flex-shrink-0"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Toggle navigation"
          >
            {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>

        <div
          className={cn(
            "xl:hidden",
            menuOpen
              ? "pointer-events-auto max-h-[560px] opacity-100"
              : "pointer-events-none max-h-0 opacity-0",
          )}
        >
          <div className="mx-6 mb-4 flex flex-col gap-3 rounded-2xl border border-border/50 bg-background/90 p-4 shadow-xl">
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => {
                const isMailto = item.to.startsWith("mailto:");
                if (isMailto) {
                  return (
                    <a
                      key={item.label}
                      href={item.to}
                      className="rounded-xl px-4 py-3 text-sm font-semibold text-muted-foreground transition hover:text-foreground"
                    >
                      {item.label}
                    </a>
                  );
                }
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        "rounded-xl px-4 py-3 text-sm font-semibold transition",
                        isActive
                          ? "bg-stronom-design/15 text-stronom-design"
                          : "text-muted-foreground hover:text-foreground",
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
            <Button
              className="w-full shadow-[0_0_30px_hsl(var(--color-design)_/_0.45)]"
              asChild
            >
              <NavLink to="/design">Launch STRONOM Design</NavLink>
            </Button>
            <Button
              variant="outline"
              className="w-full border-border/70"
              asChild
            >
              <a href="mailto:contact@stronom.com?subject=STRONOM%20Partnership">
                Contact
              </a>
            </Button>
          </div>
        </div>
      </header>
      )}

      {/* Marketing site main content - only when NOT studio app */}
      {!isStudioApp && (
        <main className="flex-1">
          <Outlet />
        </main>
      )}

      {!location.pathname.startsWith("/footwear-studio") && (
      <footer className="border-t border-border/60 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-col gap-10 px-6 py-12 text-sm text-muted-foreground lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-md space-y-4">
            <p className="text-xs uppercase tracking-[0.35em] text-stronom-agents/70">
              STRONOM
            </p>
            <p className="text-lg text-foreground">
              Modular governance for intelligent systems.
            </p>
            <p>
              STRONOM is a universal framework shaping the next generation of
              structured intelligence. Every module inherits proof-based rules,
              telemetry, and compliance primitives.
            </p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-3">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-stronom-design/70">
                Suite
              </p>
              <ul className="space-y-2">
                {stronomModules.map((module) => (
                  <li key={module.key}>
                    <NavLink to={module.slug} className="hover:text-foreground">
                      {module.title}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-stronom-design/70">
                Framework
              </p>
              <ul className="space-y-2">
                <li>
                  <NavLink to="/core" className="hover:text-foreground">
                    Core Principles
                  </NavLink>
                </li>
                <li>
                  <a href="#suite" className="hover:text-foreground">
                    Module Overview
                  </a>
                </li>
                <li>
                  <NavLink to="/design" className="hover:text-foreground">
                    STRONOM Design
                  </NavLink>
                </li>
              </ul>
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-stronom-design/70">
                Contact
              </p>
              <ul className="space-y-2">
                <li>
                  <a
                    href="mailto:contact@stronom.com"
                    className="hover:text-foreground"
                  >
                    contact@stronom.com
                  </a>
                </li>
                <li>
                  <NavLink
                    to="/design/footwear"
                    className="hover:text-foreground"
                  >
                    Footwear Studio Access
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="border-t border-border/60">
          <div className="mx-auto flex max-w-7xl flex-col gap-3 px-6 py-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <p>© {currentYear} STRONOM Technologies. All rights reserved.</p>
            <p>Structure • Governance • Proof • Trust</p>
          </div>
        </div>
      </footer>
      )}
    </div>
  );
};

export default MainLayout;
